import React, { useState } from "react";
import { MessageCircleQuestion, X, Send, Phone, Loader2, CheckCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NeedHelpWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    query: ""
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = "Enter a valid 10-digit phone number";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/callback-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSuccess(true);
        setFormData({ name: "", phone: "", email: "", query: "" });
        setTimeout(() => {
          setIsSuccess(false);
          setIsOpen(false);
        }, 3000);
      } else {
        const data = await response.json();
        setErrors({ submit: data.detail || "Failed to submit. Please try again." });
      }
    } catch (error) {
      setErrors({ submit: "Network error. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        data-testid="need-help-fab"
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-20 right-6 z-[9999] flex items-center gap-2 px-4 py-3 rounded-full 
          bg-gradient-to-r from-emerald-500 to-hw-flame-500 text-white font-medium
          shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50
          hover:scale-105 transition-all duration-300
          ${isOpen ? "scale-0 opacity-0" : "scale-100 opacity-100"}`}
      >
        <MessageCircleQuestion className="w-5 h-5" />
        <span className="hidden sm:inline">Need Help?</span>
      </button>

      {/* Modal Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:justify-end p-4 sm:p-6"
          onClick={() => !isSubmitting && setIsOpen(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Widget Panel */}
          <div
            data-testid="need-help-modal"
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:w-96 bg-slate-900 border border-slate-700 rounded-2xl 
              shadow-2xl shadow-black/50 overflow-hidden animate-in slide-in-from-bottom-4 
              sm:slide-in-from-right-4 duration-300"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700 
              bg-gradient-to-r from-emerald-500/10 to-hw-flame-500/10">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-emerald-500/20">
                  <Phone className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Request a Callback</h3>
                  <p className="text-xs text-slate-400">We'll get back to you shortly</p>
                </div>
              </div>
              <button
                data-testid="need-help-close"
                onClick={() => setIsOpen(false)}
                disabled={isSubmitting}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 
                  transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              {isSuccess ? (
                <div className="flex flex-col items-center justify-center py-8 text-center animate-in fade-in duration-300">
                  <div className="p-3 rounded-full bg-emerald-500/20 mb-4">
                    <CheckCircle className="w-10 h-10 text-emerald-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">Request Submitted!</h4>
                  <p className="text-slate-400 text-sm">
                    Our team will contact you within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1.5">
                      Full Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      data-testid="need-help-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your name"
                      className={`w-full px-3 py-2.5 bg-slate-800 border rounded-lg text-white 
                        placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50
                        transition-colors ${errors.name ? "border-red-500" : "border-slate-600"}`}
                    />
                    {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1.5">
                      Phone Number <span className="text-red-400">*</span>
                    </label>
                    <input
                      data-testid="need-help-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter your phone number"
                      className={`w-full px-3 py-2.5 bg-slate-800 border rounded-lg text-white 
                        placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50
                        transition-colors ${errors.phone ? "border-red-500" : "border-slate-600"}`}
                    />
                    {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1.5">
                      Email Address <span className="text-red-400">*</span>
                    </label>
                    <input
                      data-testid="need-help-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      className={`w-full px-3 py-2.5 bg-slate-800 border rounded-lg text-white 
                        placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50
                        transition-colors ${errors.email ? "border-red-500" : "border-slate-600"}`}
                    />
                    {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                  </div>

                  {/* Query */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1.5">
                      How can we help? <span className="text-slate-500">(Optional)</span>
                    </label>
                    <textarea
                      data-testid="need-help-query"
                      name="query"
                      value={formData.query}
                      onChange={handleChange}
                      placeholder="Briefly describe your query..."
                      rows={3}
                      className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg 
                        text-white placeholder-slate-500 focus:outline-none focus:ring-2 
                        focus:ring-emerald-500/50 transition-colors resize-none"
                    />
                  </div>

                  {/* Error Message */}
                  {errors.submit && (
                    <p className="text-red-400 text-sm text-center">{errors.submit}</p>
                  )}

                  {/* Submit Button */}
                  <button
                    data-testid="need-help-submit"
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 
                      bg-gradient-to-r from-emerald-500 to-hw-flame-500 text-white font-medium 
                      rounded-lg hover:from-emerald-600 hover:to-hw-flame-600 
                      disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Request Callback
                      </>
                    )}
                  </button>

                  <p className="text-xs text-slate-500 text-center">
                    By submitting, you agree to receive a callback from our team.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NeedHelpWidget;
